import { CommonModule } from "@angular/common";
import { Component, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ILinkableResource } from "@grainger/common/definitions/common";
import { map } from "rxjs";
import { IconListItemComponent } from "../../../components/icon-list-item/icon-list-item.component";
import { CustomAnimations } from "../../../core/animations/animations";
import { AnalyticsService } from "../../../core/services/analytics.service";

@Component({
    selector: "grainger-contract-list",
    imports: [CommonModule, IconListItemComponent],
    animations: [CustomAnimations.fadeIn("0.3s")],
    templateUrl: "./contract-list.component.html",
    styleUrl: "./contract-list.component.scss"
})
export class ContractListComponent {
    private route = inject(ActivatedRoute);
    public contractType = this.route.params.pipe(map((params) => params["contractType"] as string));
    private analyticsService = inject(AnalyticsService);

    public list = this.contractType.pipe(
        map((contractType) => {
            switch (contractType) {
                case "national-cooperative":
                    return {
                        title: "National Cooperative Contracts",
                        contracts: this.nationalCooperativeContracts
                    };
                case "federal":
                    return {
                        title: "Federal Contracts",
                        contracts: this.federalContracts
                    };
                case "healthcare-gpo":
                    return {
                        title: "Healthcare GPO Contracts",
                        contracts: this.healthcareGpoContracts
                    };
                default:
                    return {
                        title: "Contract Type Not Found",
                        contracts: []
                    };
            }
        })
    );

    public onContractClick(contract: ILinkableResource): void {
        if (contract?.url) {
            this.trackClick("Clicked on contract - " + contract.name, "contracts");
        }
    }

    public trackClick(label: string, tabName?: string): void {
        this.analyticsService.trackInteraction(
            {
                action: "click",
                location: "Contracts Page" + (tabName ? ` - ${tabName[0].toUpperCase() + tabName.slice(1)}` : ""),
                category: "button",
                label
            },
            {
                contract_type: tabName || null
            }
        );
    }

    private nationalCooperativeContracts: IContractResources[] = [
        {
            name: "OMNIA (Anthony Caraveo)",
            resources: [
                {
                    name: "OMNIA Partners - Public | COT Contract No.24007801 | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/channel/19%3A485328ebba574365bc62db4fae3fab73%40thread.tacv2/COT%20Contract%20No.24007801?groupId=84ec2e49-d0ef-421f-8e1d-07346e9445d2&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                },
                {
                    name: "OMNIA Partners - Public | Region 4 Contract No. R192002 | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/channel/19%3A5ee088d0de094989b8efc25393d6bff8%40thread.tacv2/Region%204%20Contract%20No.%20R192002?groupId=84ec2e49-d0ef-421f-8e1d-07346e9445d2&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                },
                {
                    name: "OMNIA Partners - Public | UCOP Contract No. 2018.000207 | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/channel/19%3Ad6b01fa43c6e48429bd7b2eb38a13921%40thread.tacv2/UCOP%20Contract%20No.%202018.000207?groupId=84ec2e49-d0ef-421f-8e1d-07346e9445d2&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "SOURCEWELL (Gabby Horton)",
            resources: [
                {
                    name: "Sourcewell | General | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/channel/19%3A0hr93sB-lcra73zyNue4cNKYj_dxezT0_sJSlZbBQTQ1%40thread.tacv2/General?groupId=57d5f7e5-2727-4c4d-9a98-9aebac6fcc2e&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "NASPO (Brian Dunn)",
            resources: [
                {
                    name: "NASPO ValuePoint | General | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/team/19%3AZ0fx0rKnkFeqNQ0OtvxdqSQOxUXaWJFDsVyNCJhseNI1%40thread.tacv2/conversations?groupId=19e401c7-3f17-44cf-bc3f-bacc94f5ff79&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "E&I (Patti Quintana)",
            resources: [
                {
                    name: "E&I Contract | General | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/team/19%3Ae665bbad6868475a8b6ad6b67341a817%40thread.tacv2/conversations?groupId=38663842-d0ae-427d-a05a-97e0a7383a6f&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        }
    ];

    private federalContracts: IContractResources[] = [
        {
            name: "GSA Multiple Award Schedule (MAS) (Jordan Peart)",
            resources: [
                {
                    name: "",
                    url: "",
                    description: "Coming Soon"
                }
            ]
        },
        {
            name: "GSA MRFS BPA (Jordan Peart)",
            resources: [
                {
                    name: "MRFS BPA 2024 | General | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/team/19%3A0C6786lJK7jnXKqvtxxBsUMcGRWpV1bUpjA7gukVEY01%40thread.tacv2/conversations?groupId=12888a0f-c3d8-47c3-99e8-5b563e04bf0d&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "GSA 4PL Program (Anthony Garnica)",
            resources: [
                {
                    name: "Navy BPA",
                    url: "",
                    description: "Coming Soon"
                },
                {
                    name: "Army / Air Force BPA",
                    url: "",
                    description: "Coming Soon"
                },
                {
                    name: "OCONUS BPA",
                    url: "",
                    description: "Coming Soon"
                }
            ]
        },
        {
            name: "GSA Commercial Platforms (Ryan Loughridge)",
            resources: [
                {
                    name: "All Things Federal | GSA Commercial Platforms | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/channel/19%3AGLlzl9rtBnn75pw4dbh1mChumSW8LSqlKTcZi-_JjlU1%40thread.tacv2/GSA%20Commercial%20Platforms?groupId=a3bd8604-42fe-49cd-b076-3e19c01c1fc5&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71&ngc=true&allowXTenantAccess=true"
                }
            ],
            description: `<ul class='bullet-list'>
            <li>Commercial Platforms is a "By Adoption" contract.  Currently only 13 agencies have adopted.</li>
            <li>Currently, this contract isn't available to the Air Force and not available for presentation.</li>
            </ul>`
        },
        {
            name: "GSA Requisition Channel (Jordan Peart)",
            resources: [
                {
                    name: "",
                    url: "",
                    description: "Coming Soon"
                }
            ]
        },
        {
            name: "USPS (Gabby Horton)",
            resources: [
                {
                    name: "USPS - United States Postal Service | General | Microsoft Teams",
                    url: "https://teams.microsoft.com/l/team/19%3Ap72GimJg7SrLWu-J6K0yYmWqb2RxHx4vRiBuo4Z5K4g1%40thread.tacv2/conversations?groupId=528d8f35-4db8-4442-83b9-567eaab705ba&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        }
    ];

    private healthcareGpoContracts: IContractResources[] = [
        {
            name: "Vizient (Ryan VanLopik)",
            resources: [
                {
                    name: "Vizient Committed GVP Program Page",
                    url: "https://teams.microsoft.com/l/channel/19%3A6f028d45cc7c4e24a3380dfab0a348fc%40thread.skype/2.%20Committed%20GVP?groupId=30344458-faf7-4e38-aed7-d348f1deb24f&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "Premier (Scott Peebles)",
            resources: [
                {
                    name: "Premier Contract Page",
                    url: "https://teams.microsoft.com/l/team/19%3A9dc8ec4d55b641598bf2adce25411cdd%40thread.tacv2/conversations?groupId=4d68c60b-c076-4399-b1da-b1e00489aaee&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "HealthTrust (Mike Harrod)",
            resources: [
                {
                    name: "HealthTrust Contract Page",
                    url: "https://teams.microsoft.com/l/team/19%3Abb23538442b24613bbbbb08789084459%40thread.skype/conversations?groupId=69443f45-1927-45eb-8460-745bde09c226&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                },
                {
                    name: "HCA Contract Page",
                    url: "https://teams.microsoft.com/l/team/19%3Af2303c98f8444138985247efb91d0805%40thread.tacv2/conversations?groupId=c5bd6b59-3183-4d43-bc43-693bd844201a&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        },
        {
            name: "Forum (Mike Harrod)",
            resources: [
                {
                    name: "Forum Purchasing Page",
                    url: "https://teams.microsoft.com/l/team/19%3ABUcwbfxdlHK7oJhWt8oC8i7ZVEo1GY1GQRQd6Xqp8SM1%40thread.tacv2/conversations?groupId=6e855fa7-770e-43d7-9b53-ddb2519774a0&tenantId=48d1dcb6-bccc-4365-ac7f-b937a7f7fd71"
                }
            ]
        }
    ];
}

interface IContractResources {
    name: string;
    resources: ILinkableResource[];
    description?: string;
}
